import React, { FC, useEffect, useState } from "react";
import AutoSelectAsyncList from "@pixel/core/AutoSelectAsyncList";
import { styled } from '@pixel/core/styles'
import useFieldQuery from "../hooks/useFieldQuery";
import { AutomationSearchResult } from '../types/Automations';

const AutomationFieldQueryContainer = styled('div')({
    maxWidth: '570px',
    '.MuiAutocomplete-input': {
        width: 'unset !important',
    }
})

interface AutomationFieldQueryProps {
    id?: string
    fieldEId: string
    value: any;
    onChange: (e: any, value: any) => void
    onReset: () => void,
    conditionSelected: any
    automationType: any
    companyAutomation: AutomationSearchResult
    triggerType: any
}
const AutomationFieldQuery: FC<AutomationFieldQueryProps> = ({
    id,
    fieldEId,
    value,
    onChange,
    onReset,
    conditionSelected,
    automationType,
    companyAutomation,
    triggerType
}) => {
    const [textInput, setTextInput] = useState('')
    const [page, setPage] = useState(0)
    const [lastPageLoaded, setLastPageLoaded] = useState(0)
    const [perPage, setPerPage] = useState(500)
    const [options, setOptions] = useState([])
    const [isOpened, setIsOpened] = useState(false)

    const companyEId = window._jvselfUser.companyEId;
    const selectedAutomationType = automationType !== "" ? automationType : "AUTOMATED";
    const selectedTriggerType = triggerType !== "" ? triggerType : "CANDIDATE_WORKFLOW_STATE";

    const { data, status, isLoading, isRefetching, refetch } = useFieldQuery({
        fieldEId,
        page,
        perPage,
        query: textInput,
        companyEId,
        automationType: selectedAutomationType,
        companyAutomation,
        triggerType: selectedTriggerType
    })

    const singleSelect = conditionSelected.singleSelect;

    useEffect(() => {
        setOptions([])
        onReset()
    }, [fieldEId])

    useEffect(() => {
        if (!textInput && singleSelect && value?.[0]) {
            setTextInput(value[0].displayValue); // Correcty prefill with selected value on special cases (edit mode, when field is singleSelect).
        }
    }, [value]);

    useEffect(() => {
        if (status === 'success') {
            const newOptions = data?.data?.AVAILABLE_FILTER_FIELD_VALUES.map(option => ({
                ...option,
                displayValue: option.value,
                value: option.referenceKey,
            })) ?? [];
            if (page === 0) {
                setOptions(newOptions);
            } else if (page !== lastPageLoaded) {
                setOptions(currentOptions => [...currentOptions, ...newOptions]);
                setLastPageLoaded(page);
            }
        }
    }, [data, status, page]);

    const canFetchNextPage = (data?.data?.AVAILABLE_FILTER_FIELD_VALUES?.length ?? 0) === 500;

    const handleFocus = () => {
        if (!isOpened) {
            if (!isLoading && !isRefetching) {
                refetch();
            }
            setIsOpened(true);
        }
    }

    return (
        <AutomationFieldQueryContainer
            onClick={handleFocus}
            onFocus={handleFocus}
        >
            <AutoSelectAsyncList
                id={id}
                fieldLabel=""
                key={singleSelect ? "single" : "multiple"}
                multiple={!singleSelect}
                placeholder=""
                loading={status === 'loading'}
                loadingNextPage={status === 'loading'}
                noResults={data?.data?.AVAILABLE_FILTER_FIELD_VALUES?.length === 0}
                options={options}
                inputValue={textInput}
                value={value}
                onDebouncedKeyStroke={(newInput: string) => {
                    setPage(0)
                    setTextInput(newInput)
                }}
                onChange={(event: any, newValue: Array<any>) => {
                    if (typeof onChange === 'function') {
                        if (singleSelect) {
                            onChange(event, [newValue])
                        } else {
                            onChange(event, newValue)
                        }
                    }
                    if (newValue) {
                        setTextInput(newValue.displayValue)
                    } else {
                        setTextInput('')
                    }
                }}
                onFetchNextPage={canFetchNextPage ? () => setPage(current => current + 1) : undefined}
                onClose={() => setIsOpened(false)}
                filterOptions={(x) => x}
                currentPage={page}
                disableCloseOnSelect={true}
                getOptionLabel={(option: { value: string, displayValue: string }) => option.displayValue}
                getValueLabel={(option: { value: string, displayValue: string }) => option.displayValue}
                isOptionEqualToValue={(option: { value: string, displayValue: string }, value: { referenceKey: string, value: string }) => option.value === value.referenceKey}
            />
        </AutomationFieldQueryContainer>
    )
}

export default AutomationFieldQuery
